import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import logoHorizontal from '../assets/logos/logo-horizontal.png';
import AssessmentModal from './AssessmentModal';

const HeaderWrapper = styled.header<{ $isScrolled: boolean }>`
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  background-color: ${({ $isScrolled }) => 
    $isScrolled ? 'rgba(255, 255, 255, 0.98)' : 'var(--white)'};
  box-shadow: ${({ $isScrolled }) => 
    $isScrolled ? 'var(--shadow-md)' : 'var(--shadow-sm)'};
  z-index: 9999 !important;
  transition: all var(--transition-base);
  backdrop-filter: ${({ $isScrolled }) => 
    $isScrolled ? 'blur(10px)' : 'none'};
`;

const Nav = styled.nav<{ $isScrolled: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ $isScrolled }) => 
    $isScrolled ? 'var(--spacing-sm) 0' : 'var(--spacing-md) 0'};
  transition: padding var(--transition-base), background-color var(--transition-base);
`;

const Logo = styled(Link)<{ $isScrolled: boolean }>`
  display: flex;
  align-items: center;
  height: ${({ $isScrolled }) => 
    $isScrolled ? '40px' : '50px'};
  transition: height var(--transition-base), transform var(--transition-base);
  
  img {
    height: 100%;
    width: auto;
  }

  @media (min-width: 768px) {
    height: ${({ $isScrolled }) => 
      $isScrolled ? '50px' : '60px'};
  }
`;

const NavMenu = styled.ul<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  padding: 0;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--white);
  box-shadow: var(--shadow-md);
  transform: ${({ $isOpen }) => ($isOpen ? 'translateY(0)' : 'translateY(-100%)')};
  opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'all' : 'none')};
  transition: all var(--transition-base);

  @media (min-width: 768px) {
    flex-direction: row;
    position: static;
    transform: none;
    opacity: 1;
    pointer-events: all;
    box-shadow: none;
    gap: var(--spacing-xl);
  }
`;

const NavItem = styled.li`
  position: relative;
  
  @media (min-width: 768px) {
    &.has-dropdown:hover .services-dropdown {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }
`;

const NavLink = styled(Link)<{ $isActive: boolean }>`
  display: block;
  padding: var(--spacing-md) var(--spacing-lg);
  color: ${({ $isActive }) => ($isActive ? 'var(--davinci-blue)' : 'var(--davinci-grey)')};
  font-weight: var(--font-weight-medium);
  transition: color var(--transition-fast), font-weight var(--transition-base);

  &:hover {
    color: var(--davinci-blue);
    font-weight: var(--font-weight-bold);
  }

  @media (min-width: 768px) {
    padding: var(--spacing-sm) 0;
  }
`;

const ServicesDropdown = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  position: static;
  background-color: var(--light-grey);
  
  @media (min-width: 768px) {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%) translateY(-10px);
    background-color: var(--white);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    min-width: 200px;
    padding: var(--spacing-sm) 0;
    margin-top: 5px;
    z-index: 1000;
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease;
    display: block !important;
    
    /* Invisible bridge to maintain hover */
    &::before {
      content: '';
      position: absolute;
      top: -10px;
      left: 0;
      right: 0;
      height: 10px;
    }
    
    /* Arrow indicator */
    &::after {
      content: '';
      position: absolute;
      top: -8px;
      left: 50%;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-bottom: 8px solid var(--white);
    }
  }
`;

const DropdownLink = styled(Link)`
  display: block;
  padding: var(--spacing-sm) var(--spacing-xl);
  color: var(--davinci-grey);
  font-size: 0.95rem;
  font-weight: var(--font-weight-medium);
  transition: all var(--transition-fast);
  white-space: nowrap;

  &:hover {
    color: var(--davinci-blue);
    background-color: rgba(59, 130, 206, 0.05);
    padding-left: calc(var(--spacing-xl) + 4px);
  }

  @media (min-width: 768px) {
    padding: var(--spacing-sm) var(--spacing-lg);
    
    &:hover {
      padding-left: calc(var(--spacing-lg) + 4px);
    }
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 30px;
  height: 24px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  @media (min-width: 768px) {
    display: none;
  }

  span {
    display: block;
    width: 100%;
    height: 3px;
    background-color: var(--davinci-grey);
    transition: all var(--transition-fast);
  }

  &.active {
    span:nth-child(1) {
      transform: rotate(45deg) translate(5px, 5px);
    }
    span:nth-child(2) {
      opacity: 0;
    }
    span:nth-child(3) {
      transform: rotate(-45deg) translate(7px, -6px);
    }
  }
`;

const CTAButtonBase = `
  display: inline-block;
  background-color: var(--davinci-blue);
  color: white !important;
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: 4px;
  font-weight: var(--font-weight-bold);
  font-size: var(--fs-sm);
  transition: background-color var(--transition-base), transform var(--transition-base), box-shadow var(--transition-base);
  text-decoration: none;
  border: none;
  cursor: pointer;
`;

const CTAButton = styled(Link)`
  ${CTAButtonBase}

  &:hover {
    background-color: var(--davinci-denim);
    color: white !important;
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
  
  &:focus, &:active, &:visited {
    color: white !important;
  }

  @media (min-width: 768px) {
    padding: var(--spacing-sm) var(--spacing-lg);
    font-size: var(--fs-base);
  }
`;

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const isScrolledRef = useRef(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;
  const isServicesActive = () => location.pathname.startsWith('/services');

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setServicesDropdownOpen(false);
  };

  const toggleServicesDropdown = () => {
    setServicesDropdownOpen(!servicesDropdownOpen);
  };

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const next = y > 0;
      if (isScrolledRef.current !== next) {
        isScrolledRef.current = next;
        setIsScrolled(next);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true } as EventListenerOptions);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll as any);
  }, []);

  return (
    <>
    <HeaderWrapper $isScrolled={isScrolled}>
      <div className="container">
        <Nav $isScrolled={isScrolled}>
          <Logo to="/" onClick={() => setMobileMenuOpen(false)} $isScrolled={isScrolled}>
            <img src={logoHorizontal} alt="Davinci AI Solutions" />
          </Logo>

          <MobileMenuButton
            className={mobileMenuOpen ? 'active' : ''}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </MobileMenuButton>

          <NavMenu $isOpen={mobileMenuOpen}>
            <NavItem>
              <NavLink 
                to="/about" 
                $isActive={isActive('/about')}
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </NavLink>
            </NavItem>
            
            <NavItem className="has-dropdown">
              <NavLink
                to="/services"
                $isActive={isServicesActive()}
                onClick={(e) => {
                  if (window.innerWidth < 768) {
                    toggleServicesDropdown();
                    e.preventDefault();
                  } else {
                    setMobileMenuOpen(false);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                Services ▾
              </NavLink>
              <ServicesDropdown className="services-dropdown" $isOpen={servicesDropdownOpen}>
                <DropdownLink to="/services/ai-solutions" onClick={() => {
                  setMobileMenuOpen(false);
                  setServicesDropdownOpen(false);
                }}>
                  AI Solutions
                </DropdownLink>
                <DropdownLink to="/services/managed-it" onClick={() => {
                  setMobileMenuOpen(false);
                  setServicesDropdownOpen(false);
                }}>
                  Managed IT
                </DropdownLink>
                <DropdownLink to="/services/helpdesk" onClick={() => {
                  setMobileMenuOpen(false);
                  setServicesDropdownOpen(false);
                }}>
                  Helpdesk Support
                </DropdownLink>
                <DropdownLink to="/services/it-projects" onClick={() => {
                  setMobileMenuOpen(false);
                  setServicesDropdownOpen(false);
                }}>
                  IT Projects
                </DropdownLink>
              </ServicesDropdown>
            </NavItem>

            <NavItem>
              <NavLink 
                to="/contact" 
                $isActive={isActive('/contact')}
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </NavLink>
            </NavItem>
          </NavMenu>
          
        <button 
          onClick={() => setShowAssessment(true)}
          style={{
            display: 'inline-block',
            backgroundColor: 'var(--davinci-blue)',
            color: 'white',
            padding: 'var(--spacing-sm) var(--spacing-lg)',
            borderRadius: '4px',
            fontWeight: 'var(--font-weight-bold)',
            fontSize: 'var(--fs-sm)',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Get AI Readiness Score
        </button>
      </Nav>
    </div>
    </HeaderWrapper>
      <AssessmentModal isOpen={showAssessment} onClose={() => setShowAssessment(false)} />
    </>
  );
};

export default Header;
